import { defineStore } from 'pinia'
import { ref } from 'vue'

import { ventasApi } from '@/api/ventas'
import { useTurnoStore } from '@/stores/turno'
import type { Venta, VentaInput, VentaLoteInput, VentaLoteResponse } from '@/types/api'

export const useVentasStore = defineStore('ventas', () => {
  const recientes = ref<Venta[]>([])
  const cargando = ref(false)
  // Anulaciones ya descontadas del turno: la respuesta local y su eco por WS
  // llegan por separado y solo una debe restar.
  const anuladasProcesadas = new Set<number>()

  async function cargarRecientes(limit = 20) {
    cargando.value = true
    try {
      recientes.value = await ventasApi.recientes(limit)
      for (const v of recientes.value) if (v.anulada) anuladasProcesadas.add(v.id)
    } finally {
      cargando.value = false
    }
  }

  async function registrar(payload: VentaInput) {
    const venta = await ventasApi.registrar(payload)
    // Optimistic; the WS event will also arrive and dedupe in agregar().
    agregar(venta)
    return venta
  }

  /** Ticket con varias líneas: una venta por línea, mismo ticket_id. */
  async function registrarLote(payload: VentaLoteInput): Promise<VentaLoteResponse> {
    const r = await ventasApi.registrarLote(payload)
    for (const v of r.ventas) agregar(v)
    return r
  }

  function agregar(venta: Venta): boolean {
    if (recientes.value.some((v) => v.id === venta.id)) return false
    recientes.value = [venta, ...recientes.value].slice(0, 50)
    // Único punto que deduplica por id: aquí (y solo aquí) se cuenta la venta
    // en el turno, para no sumar dos veces la venta local y su eco por WS.
    useTurnoStore().contarVenta(venta)
    return true
  }

  async function anular(id: number, motivo: string) {
    const venta = await ventasApi.anular(id, motivo)
    aplicarAnulacion(venta)
    return venta
  }

  /** Marca la venta como anulada en el feed y la descuenta del turno una sola vez. */
  function aplicarAnulacion(venta: Venta): boolean {
    recientes.value = recientes.value.map((v) => (v.id === venta.id ? venta : v))
    if (anuladasProcesadas.has(venta.id)) return false
    anuladasProcesadas.add(venta.id)
    useTurnoStore().descontarVenta(venta)
    return true
  }

  return { recientes, cargando, cargarRecientes, registrar, registrarLote, agregar, anular, aplicarAnulacion }
})
